/**
 * The built-in English function-word core (plan §2.2).
 *
 * @remarks
 * Deliberately minimal and game-free: articles and politeness filler (stripped
 * as stopwords), pronoun tokens (resolved against recency), and — imported
 * from the splitter that owns them — the chain conjunctions. Game nouns and
 * verbs NEVER ship built-in; those arrive through the authored
 * `LexiconDefinition`. Compilation rejects any authored token that collides
 * with a reserved word here, because such a collision would silently corrupt
 * stripping, splitting, or pronoun resolution.
 *
 * @packageDocumentation
 */

import { CHAIN_CONJUNCTION_TOKENS } from '../tokenize/split-on-conjunctions.js';

/** Articles and determiners stripped before grammar matching. */
export const BUILTIN_ARTICLES = ['a', 'an', 'some', 'the'] as const;

/**
 * Politeness/filler words with no grammatical weight — EMPTY since D31:
 * `please` moved to {@link SOFT_FILLER_WORDS} (yields to vocabulary, and a
 * bare "please" can now reach the social register instead of blank_input).
 */
export const BUILTIN_FILLER_WORDS = [] as const;

/**
 * The parser-owned fallback intent token for bare directions (`north`, `n`)
 * when the authored vocabulary declares no `movementIntentToken`.
 *
 * @remarks
 * Collision-proof by construction: the `:` can never survive character folding
 * (players cannot type it into a token), and compilation rejects any authored
 * token containing `:`. Consumers that ignore the field see this token loudly
 * at their intent-narrowing boundary rather than losing bare directions
 * silently. Authoring `movementIntentToken` (e.g. `go`) is the recommended
 * path — it unifies `north` with `go north` under one canonical intent.
 */
export const BUILTIN_MOVEMENT_INTENT = 'builtin:move';

/**
 * Pronoun tokens resolved against the recency list (plan §4). D226 (E12)
 * adds `him` and `her`: a player who has just examined the old man types
 * `x him`, and the play-test found every pack answering "What him?". They
 * resolve as `it` does — to the most recent thing — wherever the catalog
 * carries no gender, and the parser invents none; the person the player just
 * spoke of is who they mean. D245: where an entry DECLARES its pronoun, it
 * answers to that word alone, and `him`/`her` prefer it (`resolve-pronoun.ts`).
 */
export const BUILTIN_PRONOUN_TOKENS = ['it', 'them', 'him', 'her'] as const;

/** The pronoun words the parser recognizes (`it`, `them`, `him`, `her`). */
export type PronounWord = (typeof BUILTIN_PRONOUN_TOKENS)[number];

/**
 * Conversational filler stripped ONLY when the word resolves to nothing (D26).
 *
 * @remarks
 * "what is the weather like", "open the door right now" — trailing chatter
 * that defeats tail-anchored noun resolution. Unlike {@link BUILTIN_STOPWORDS}
 * these are deliberately NOT reserved: a vocabulary may claim any of them
 * (`right` as a direction, `like` as an intent alias), and a claimed word is
 * never stripped — the orchestrator checks the parse call's legal-word set
 * (compiled vocabulary + entity catalog) before dropping one. Soft, yielding,
 * deterministic.
 */
export const SOFT_FILLER_WORDS: ReadonlySet<string> = new Set([
	'actually',
	'just',
	'like',
	'now',
	'please',
	'really',
	'right',
]);

/** The default stopword core: articles + filler. Callers extend via `LexiconDefinition.stopwords`. */
export const BUILTIN_STOPWORDS: readonly string[] = [...BUILTIN_ARTICLES, ...BUILTIN_FILLER_WORDS];

/**
 * Every reserved function word an authored token may not claim: stopwords,
 * pronouns, and the chain conjunctions.
 */
export const RESERVED_FUNCTION_WORDS: ReadonlySet<string> = new Set([
	...BUILTIN_STOPWORDS,
	...BUILTIN_PRONOUN_TOKENS,
	...CHAIN_CONJUNCTION_TOKENS,
]);
